<template>
  <section class="empire-summary">
    <header class="widget-header">
      <h2>Empire Summary</h2>
      <p class="kicker">Executive ledger</p>
    </header>

    <div v-if="shieldActive" class="shield-banner" role="status">
      <div class="shield-scan" aria-hidden="true"></div>
      <div class="shield-copy">
        <span class="shield-label">
          <i class="fas fa-shield-halved" aria-hidden="true"></i>
          Peace Shield Active
        </span>
        <span class="shield-badge">{{ shieldBadge }}</span>
      </div>
      <span class="shield-timer">{{ shieldCountdown }}</span>
    </div>

    <div class="net-worth">
      <span class="net-label">Net Worth</span>
      <span class="net-value">{{ formatNumber(netWorth) }}</span>
    </div>

    <ul class="resource-list">
      <li v-for="row in resourceRows" :key="row.key" class="resource-row">
        <span class="resource-name">
          <i :class="row.icon" :style="{ color: row.color }"></i>
          {{ row.name }}
        </span>
        <span class="resource-stock">{{ formatNumber(row.stock) }}</span>
        <span class="resource-rate" :class="rateClass(row.rate)">
          {{ formatRate(row.rate) }}
        </span>
      </li>
    </ul>
  </section>
</template>

<script setup>
import { computed, onMounted, onUnmounted, ref, watch } from "vue";
import { useAuthStore } from "../store/auth";
import { useGameStore } from "../store/game";
import {
  RESOURCE_DISPLAY_KEYS,
  projectResourceStocks,
  resourceDisplayMap,
} from "../utils/resourceDisplay";
import {
  formatShieldCountdown,
  isCityProtected,
  shieldBadgeLabel,
} from "../utils/cityShield";

const authStore = useAuthStore();
const gameStore = useGameStore();
const projected = ref({});
const nowMs = ref(Date.now());
let tickerId = null;

const netWorth = computed(() => {
  if (gameStore.empireNetWorth != null) {
    return Number(gameStore.empireNetWorth) || 0;
  }
  return Number(authStore.user?.empire_net_worth) || 0;
});

function projectNow() {
  const base = gameStore.globalAggregation;
  if (!base) {
    projected.value = {};
    return;
  }
  projected.value = projectResourceStocks(
    base,
    base.rates,
    base.last_ticked_at,
    gameStore.clockOffsetMs,
    gameStore.tickIntervalMs || 60 * 60 * 1000
  );
}

watch(
  () => [gameStore.globalAggregation, gameStore.clockOffsetMs],
  () => {
    projectNow();
  },
  { immediate: true, deep: true }
);

onMounted(() => {
  tickerId = setInterval(() => {
    nowMs.value = Date.now();
    projectNow();
  }, 1000);
});

onUnmounted(() => {
  if (tickerId) clearInterval(tickerId);
});

const resourceRows = computed(() => {
  const stocks = projected.value || {};
  const rates = gameStore.globalAggregation?.rates || {};
  return RESOURCE_DISPLAY_KEYS.map((key) => {
    const display = resourceDisplayMap[key];
    const rateEntry = rates[key];
    const rate =
      rateEntry && typeof rateEntry === "object"
        ? Number(rateEntry.net) || 0
        : Number(rateEntry) || 0;
    return {
      key,
      name: display.name,
      icon: display.icon,
      color: display.color,
      stock: Math.max(0, Math.floor(Number(stocks[key]) || 0)),
      rate,
    };
  });
});

function formatNumber(num) {
  const n = Number(num) || 0;
  if (n >= 1000000) return (n / 1000000).toFixed(1) + "M";
  if (n >= 1000) return (n / 1000).toFixed(1) + "K";
  return String(Math.floor(n));
}

function formatRate(net) {
  const n = Math.round(Number(net) || 0);
  if (n === 0) return "0/h";
  return `${n > 0 ? "+" : ""}${n}/h`;
}

function rateClass(net) {
  const n = Number(net) || 0;
  if (n > 0) return "positive";
  if (n < 0) return "negative";
  return "";
}

const primaryCity = computed(() =>
  Array.isArray(gameStore.cities) && gameStore.cities.length
    ? gameStore.cities[0]
    : null
);

const shieldUntil = computed(
  () =>
    primaryCity.value?.protected_until ||
    gameStore.globalAggregation?.protected_until ||
    null
);

const shieldKind = computed(
  () =>
    primaryCity.value?.shield_kind ||
    gameStore.globalAggregation?.shield_kind ||
    null
);

const shieldActive = computed(() =>
  isCityProtected(shieldUntil.value, gameStore.clockOffsetMs, nowMs.value)
);

const shieldCountdown = computed(() =>
  formatShieldCountdown(shieldUntil.value, gameStore.clockOffsetMs, nowMs.value)
);

const shieldBadge = computed(() => shieldBadgeLabel(shieldKind.value));

</script>

<style scoped>
.empire-summary {
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  padding: 0.9rem 1rem 1rem;
  box-sizing: border-box;
  color: #e7ecf1;
}

.widget-header h2 {
  margin: 0;
  font-family: "Cinzel", serif;
  font-size: 0.78rem;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: #c9b48a;
}

.kicker {
  margin: 0.15rem 0 0;
  font-size: 0.72rem;
  color: #88c0d0;
}

.shield-banner {
  position: relative;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.65rem;
  margin: 0.7rem 0 0;
  padding: 0.65rem 0.75rem;
  border-radius: 6px;
  border: 1px solid rgba(0, 240, 255, 0.45);
  background:
    linear-gradient(180deg, rgba(0, 40, 55, 0.72), rgba(6, 12, 24, 0.88));
  box-shadow: 0 0 12px rgba(0, 240, 255, 0.25), inset 0 0 8px rgba(0, 240, 255, 0.15);
  animation: shield-pulse 2.4s ease-in-out infinite;
}

.shield-scan {
  position: absolute;
  inset: 0;
  pointer-events: none;
  background: repeating-linear-gradient(
    to bottom,
    rgba(0, 240, 255, 0.08) 0px,
    rgba(0, 240, 255, 0.08) 1px,
    transparent 1px,
    transparent 7px
  );
  animation: shield-scan 3.6s linear infinite;
  opacity: 0.55;
}

.shield-copy {
  position: relative;
  z-index: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
}

.shield-label {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  font-family: "Cinzel", serif;
  font-size: 0.72rem;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #00f0ff;
}

.shield-badge {
  align-self: flex-start;
  font-size: 0.62rem;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #9eefff;
  background: rgba(0, 240, 255, 0.12);
  border: 1px solid rgba(0, 240, 255, 0.35);
  border-radius: 999px;
  padding: 0.12rem 0.45rem;
}

.shield-timer {
  position: relative;
  z-index: 1;
  flex-shrink: 0;
  font-family: "Exo 2", monospace;
  font-variant-numeric: tabular-nums;
  font-size: 1.05rem;
  font-weight: 700;
  color: #e7ffff;
  text-shadow: 0 0 8px rgba(0, 240, 255, 0.65);
}

@keyframes shield-pulse {
  0%,
  100% {
    box-shadow: 0 0 12px rgba(0, 240, 255, 0.25), inset 0 0 8px rgba(0, 240, 255, 0.15);
  }
  50% {
    box-shadow: 0 0 22px rgba(0, 240, 255, 0.5), inset 0 0 14px rgba(0, 240, 255, 0.3);
  }
}

@keyframes shield-scan {
  0% {
    background-position: 0 -24px;
  }
  100% {
    background-position: 0 24px;
  }
}

@media (max-width: 768px) {
  .shield-banner {
    flex-direction: column;
    align-items: flex-start;
  }
}

.net-worth {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.15rem;
  margin: 0.85rem 0 0.75rem;
  padding: 0.65rem 0.75rem;
  background: rgba(0, 0, 0, 0.5);
  border: 1px solid #333;
  border-radius: 4px;
}

.net-label {
  font-size: 0.62rem;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: #888;
}

.net-value {
  font-family: "Cinzel", serif;
  font-size: 1.65rem;
  font-weight: 800;
  color: #ffd700;
  text-shadow: 0 0 10px rgba(255, 215, 0, 0.35);
  line-height: 1.15;
}

.resource-list {
  list-style: none;
  margin: 0;
  padding: 0;
  overflow: auto;
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  min-height: 0;
  flex: 1;
}

.resource-row {
  display: grid;
  grid-template-columns: minmax(0, 1.4fr) minmax(0, 0.8fr) minmax(0, 0.7fr);
  gap: 0.4rem;
  align-items: center;
  padding: 0.28rem 0.4rem;
  background: rgba(0, 240, 255, 0.06);
  border: 1px solid rgba(0, 240, 255, 0.18);
  border-radius: 4px;
  font-size: 0.78rem;
}

.resource-name {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  min-width: 0;
}

.resource-name i {
  width: 1rem;
  text-align: center;
}

.resource-stock {
  font-weight: 700;
  color: #fff;
  text-align: right;
}

.resource-rate {
  text-align: right;
  color: #8892b0;
  font-variant-numeric: tabular-nums;
}

.resource-rate.positive {
  color: #a3be8c;
}

.resource-rate.negative {
  color: #bf616a;
}
</style>
